import type { Logger, LoggerConfig, LoggerOptions } from "@/types/Logger";

/**
 * Abstract Logger Service (Port)
 *
 * Factory service for creating namespaced loggers.
 * Inspired by @libp2p/logger with namespace-based filtering.
 *
 * @example
 * ```typescript
 * const logger = loggerService.createLogger({ namespace: 'six3ro:auth' });
 * logger.info('User logged in', { userId: '123' });
 * logger.error('Login failed', error);
 * ```
 */
export abstract class LoggerService {
  /**
   * Create a namespaced logger instance
   *
   * @param options - Logger configuration options
   * @returns A logger instance for the specified namespace
   */
  abstract createLogger(options: LoggerOptions): Logger;

  /**
   * Configure global logger settings
   *
   * @param config - Global logger configuration
   * @example
   * ```typescript
   * // Enable all p2p logs
   * loggerService.configure({ enabled: 'six3ro:p2p:*' });
   *
   * // Set global log level
   * loggerService.configure({ level: LogLevel.DEBUG });
   * ```
   */
  abstract configure(config: LoggerConfig): void;

  /**
   * Enable logging for namespaces matching a pattern
   *
   * @param pattern - Namespace pattern (e.g., 'six3ro:*', 'six3ro:p2p:*')
   */
  abstract enable(pattern: string): void;

  /**
   * Disable logging for namespaces matching a pattern
   *
   * @param pattern - Namespace pattern (e.g., 'six3ro:*', 'six3ro:p2p:*')
   */
  abstract disable(pattern: string): void;

  /**
   * Disable all logging
   */
  abstract disableAll(): void;

  /**
   * Refresh configuration from localStorage
   * Useful when localStorage values change at runtime
   */
  abstract refreshFromStorage(): void;
}
